<!-- TEMPLATE -->
<template>
  <transition name="fade">
    <div class="dean-load-overlay">
      <dean-spinner
        v-if="!cssSpinner"
        class="loader"
        :size="size"
        :animation="spinnerType"
      ></dean-spinner>
      <div v-if="cssSpinner" class="lds-roller">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <dean-overlay v-if="overlay" light></dean-overlay>
    </div>
  </transition>
</template>

<!-- SCRIPT -->
<script lang="ts">
// VUE
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

// SVG
import bouncingLinesSpinner from '../svg/spinners/bouncing-lines.svg';
import fadingLinesSpinner from '../svg/spinners/fading-lines.svg';
import fullCircleSpinner from '../svg/spinners/full-circle.svg';
import halfCircleSpinner from '../svg/spinners/half-circle.svg';
import jumpingLinesSpinner from '../svg/spinners/jumping-lines.svg';
import pulseFadeLinesSpinner from '../svg/spinners/pulse-fade-lines.svg';
import pulsingLinesSpinner from '../svg/spinners/pulsing-lines.svg';
import quarterCircleSpinner from '../svg/spinners/quarter-circle.svg';

// COMPONENTS
import deanOverlay from './deanOverlay.vue';
import deanSpinner from './deanSpinner.vue';

@Component({
  components: {
    deanOverlay,
    deanSpinner
  }
})
export default class deanLoadOverlay extends Vue {
  // DATA
  bouncingLinesSpinner: object = bouncingLinesSpinner;
  fadingLinesSpinner: object = fadingLinesSpinner;
  fullCircleSpinner: object = fullCircleSpinner;
  halfCircleSpinner: object = halfCircleSpinner;
  jumpingLinesSpinner: object = jumpingLinesSpinner;
  pulseFadeLinesSpinner: object = pulseFadeLinesSpinner;
  pulsingLinesSpinner: object = pulsingLinesSpinner;
  quarterCircleSpinner: object = quarterCircleSpinner;

  // PROPS
  @Prop() data!: object;
  @Prop({ default: false }) bouncingLines!: boolean;
  @Prop({ default: false }) fadingLines!: boolean;
  @Prop({ default: false }) fullCircle!: boolean;
  @Prop({ default: false }) halfCircle!: boolean;
  @Prop({ default: false }) jumpingLines!: boolean;
  @Prop({ default: false }) pulseFadeLines!: boolean;
  @Prop({ default: false }) pulsingLines!: boolean;
  @Prop({ default: false }) quarterCircle!: boolean;
  @Prop({ default: '50' }) size!: string;
  @Prop({ default: true }) cssSpinner!: boolean;
  @Prop() animation!: object;
  @Prop({ default: true }) overlay!: boolean;

  // COMPUTED
  get spinnerType() {
    let value = this.fullCircleSpinner;
    if (this.bouncingLines) value = this.bouncingLinesSpinner;
    else if (this.fadingLines) value = this.fadingLinesSpinner;
    else if (this.fullCircle) value = this.fullCircleSpinner;
    else if (this.halfCircle) value = this.halfCircleSpinner;
    else if (this.jumpingLines) value = this.jumpingLinesSpinner;
    else if (this.pulseFadeLines) value = this.pulseFadeLinesSpinner;
    else if (this.pulsingLines) value = this.pulsingLinesSpinner;
    else if (this.quarterCircle) value = this.quarterCircleSpinner;
    else value = this.fullCircleSpinner;
    if (this.animation) {
      value = this.animation;
    }
    return value;
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>
// IMPORT
@import '../scss/vars';

.dean-load-overlay {
  @include display-flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  overflow: hidden;
  z-index: 996;
  @include border-radius(10px);

  .loader {
    z-index: 996;
  }

  .lds-ring {
    display: inline-block;
    position: relative;
    width: 64px;
    height: 64px;
    z-index: 996;
  }
  .lds-ring div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 51px;
    height: 51px;
    margin: 6px;
    border: 6px solid var(--color-primary, $color-primary);
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: var(--color-primary, $color-primary) transparent
      transparent transparent;
  }
  .lds-ring div:nth-child(1) {
    animation-delay: -0.45s;
  }
  .lds-ring div:nth-child(2) {
    animation-delay: -0.3s;
  }
  .lds-ring div:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .lds-roller {
    display: inline-block;
    position: relative;
    width: 64px;
    height: 64px;
    z-index: 996;
  }
  .lds-roller div {
    animation: lds-roller 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    transform-origin: 32px 32px;
  }
  .lds-roller div:after {
    content: ' ';
    display: block;
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-primary, $color-primary);
    margin: -3px 0 0 -3px;
  }
  .lds-roller div:nth-child(1) {
    animation-delay: -0.036s;
  }
  .lds-roller div:nth-child(1):after {
    top: 50px;
    left: 50px;
  }
  .lds-roller div:nth-child(2) {
    animation-delay: -0.072s;
  }
  .lds-roller div:nth-child(2):after {
    top: 54px;
    left: 45px;
  }
  .lds-roller div:nth-child(3) {
    animation-delay: -0.108s;
  }
  .lds-roller div:nth-child(3):after {
    top: 57px;
    left: 39px;
  }
  .lds-roller div:nth-child(4) {
    animation-delay: -0.144s;
  }
  .lds-roller div:nth-child(4):after {
    top: 58px;
    left: 32px;
  }
  .lds-roller div:nth-child(5) {
    animation-delay: -0.18s;
  }
  .lds-roller div:nth-child(5):after {
    top: 57px;
    left: 25px;
  }
  .lds-roller div:nth-child(6) {
    animation-delay: -0.216s;
  }
  .lds-roller div:nth-child(6):after {
    top: 54px;
    left: 19px;
  }
  .lds-roller div:nth-child(7) {
    animation-delay: -0.252s;
  }
  .lds-roller div:nth-child(7):after {
    top: 50px;
    left: 14px;
  }
  .lds-roller div:nth-child(8) {
    animation-delay: -0.288s;
  }
  .lds-roller div:nth-child(8):after {
    top: 45px;
    left: 10px;
  }
  @keyframes lds-roller {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  // https://loading.io/css/
}
</style>
